import { memo } from 'react'
import { Handle, Position } from 'reactflow'
import { GraduationCap } from 'lucide-react'
import {
  STATUS_COLORS,
  DIFFICULTY_BADGE,
  DIFFICULTY_LABELS,
  getRemainingToMaster,
  getMasteryPercent
} from './knowledgeMapUtils'

/**
 * Student knowledge-map node. Adopts the richer admin KnowledgeNodeCard layout (domain header, skill
 * name, footer badges, selected ring) but is colored by the student's OWN mastery status instead of
 * test type, and surfaces personal progress (proficiency %, progress-to-70% bar, remaining-to-master)
 * rather than question counts / DOK. `selected` mirrors the admin canvas's selectedNodeId highlight.
 */
function MasteryNodeComponent({ data, selected }) {
  const mastery = data.mastery || {
    status: 'UNVISITED',
    totalAttempts: 0,
    correctAttempts: 0,
    score: 0
  }
  const colors = STATUS_COLORS[mastery.status] || STATUS_COLORS.UNVISITED
  const badge = DIFFICULTY_BADGE[data.difficulty] || DIFFICULTY_BADGE.M
  const diffLabel = DIFFICULTY_LABELS[data.difficulty] || data.difficulty

  const score = mastery.score || 0
  const masteryPct = getMasteryPercent(score)
  const { label: remainingLabel } = getRemainingToMaster(score, data.difficulty, mastery.status)
  const hasAttempts = mastery.totalAttempts > 0

  return (
    <>
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-2 !h-2" />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-400 !w-2 !h-2" />
      <div
        className={`w-[240px] rounded-lg px-3 py-2 shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md ${
          selected ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-900' : ''
        }`}
        role="button"
        tabIndex={0}
        aria-label={`${data.skill} — ${mastery.status.toLowerCase().replaceAll('_', ' ')}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.currentTarget.click()
          }
        }}
        style={{
          backgroundColor: colors.bg,
          border: `2px solid ${colors.border}`
        }}
      >
        {/* Header: domain + status dot + tutor-practice marker */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 truncate">
            {data.domain}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0">
            {data.hasTutorPractice && (
              <GraduationCap
                className="w-3.5 h-3.5 text-indigo-600"
                aria-label="Has tutor practice"
              />
            )}
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: colors.dot }}
              title={colors.text}
            />
          </span>
        </div>

        {/* Skill name */}
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight mb-2">
          {data.skill}
        </div>

        {/* Footer: difficulty badge + proficiency */}
        <div className="flex items-center justify-between gap-2">
          <span
            className="px-1.5 py-0.5 rounded text-[10px] font-medium"
            style={{ backgroundColor: badge.bg, color: badge.text }}
          >
            {diffLabel}
          </span>
          {hasAttempts && (
            <span className="text-xs font-medium" style={{ color: colors.border }}>
              {(score * 100).toFixed(0)}%
            </span>
          )}
        </div>

        {/* Personal mastery progress */}
        {hasAttempts && (
          <>
            <div className="mt-1.5 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${masteryPct}%`, backgroundColor: colors.border }}
              />
            </div>
            {remainingLabel && (
              <div className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                {remainingLabel}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export const MasteryNode = memo(MasteryNodeComponent)
